/**
 * The authoritative protected-route definition, shared by the middleware and
 * the navigation catalogue.
 *
 * This list used to live as a literal inside `src/proxy.ts`, while the two
 * navigation trees each hand-copied a *different, shorter* approximation of it
 * (`["message","ticket","settings"]`). Nav filtering and route protection could
 * therefore disagree, and did: `/portfolio`, `/referrals`, `/saved`, `/bids`
 * and `/notifications` are all middleware-protected and appeared in no nav
 * auth rule at all.
 *
 * Moving the data here and having BOTH sides import it makes the navigation's
 * auth predicate *derived* rather than copied — the property Phase 01 requires
 * and the only way the two can no longer drift.
 *
 * `src/proxy.ts` remains the actual protection gate. Nothing here enforces
 * anything; navigation filtering is cosmetic and must never be treated as
 * security. This module only lets the cosmetic layer agree with the real one.
 *
 * Edge-runtime safe: no imports, no Node built-ins — `proxy.ts` runs as Next
 * middleware and cannot pull in anything heavier.
 */

/**
 * Prefix-matched protected paths (locale segment already stripped).
 * `startsWith`, so sub-routes are covered — `/settings/profile` included.
 */
export const PROTECTED_ROUTE_PREFIXES = [
  "/dashboard",
  "/settings",
  "/wallet",
  "/portfolio",
  "/referrals",
  "/message",
  "/notifications",
  "/saved",
  "/create-nft",
  "/auctions/create",
  "/galleries/create",
  "/ticket",
  // Gift-card links arrive by email/SMS and can land on a signed-out browser.
  // Gating them here means the visitor is bounced to login with callbackUrl
  // pointing back at the card, instead of seeing a redeem form that would fail.
  "/redeem",
  "/drops/create",
] as const;

/**
 * Exact/pattern-matched protected paths.
 *
 * The first two are deliberately anchored: `/auctions` and `/bids` are the
 * signed-in "my auctions" / "my bids" indexes, while `/auctions/[slug]` is a
 * PUBLIC auction detail page. A prefix match here would gate the public
 * listing behind login. Any change to that anchoring is a product decision.
 *
 * None carry the `g` flag, so `.test()` is stateless and these can be shared
 * across calls safely.
 */
export const PROTECTED_ROUTE_PATTERNS = [
  /^\/auctions(?:\/)?$/,
  /^\/bids(?:\/)?$/,
  /^\/auctions\/[^/]+\/edit(?:\/|$)/,
  /^\/galleries\/[^/]+\/edit(?:\/|$)/,
  // Anchored on `/edit` right after the slug, so it does not also swallow the
  // public `/drops/[slug]` detail page.
  /^\/drops\/[^/]+\/edit(?:\/|$)/,
  /^\/drops\/[^/]+\/items\/create(?:\/|$)/,
  /^\/drops\/[^/]+\/items\/[^/]+\/edit(?:\/|$)/,
  // `/collection` is the PUBLIC browse listing and `/collection/[contract]` a
  // public detail page, so this cannot be a prefix entry. Only the import flow
  // is gated: every endpoint it uses is bearer-authenticated and owner-scoped,
  // so a signed-out visitor would get a form that cannot submit above a list
  // that cannot load.
  /^\/collection\/import(?:\/|$)/,
] as const;

/**
 * Routes that are strictly for non-authenticated users.
 */
export const AUTH_ROUTES = ["/auth/login"] as const;

/**
 * Is this locale-stripped path gated by the middleware?
 *
 * Semantics are byte-for-byte the disjunction `proxy.ts` previously inlined —
 * prefix list OR pattern list. Kept as one function so the navigation
 * predicate and the gate cannot evaluate the same path differently.
 */
export function isProtectedPath(pathWithoutLocale: string): boolean {
  return (
    PROTECTED_ROUTE_PREFIXES.some((route) => pathWithoutLocale.startsWith(route)) ||
    PROTECTED_ROUTE_PATTERNS.some((pattern) => pattern.test(pathWithoutLocale))
  );
}

/**
 * Is this locale-stripped path a guest-only auth route?
 */
export function isAuthOnlyPath(pathWithoutLocale: string): boolean {
  return AUTH_ROUTES.some((route) => pathWithoutLocale.startsWith(route));
}
